import type { JSX, ReactNode, Ref } from "react";
import { cx } from "./classNames";
import styles from "./ui.module.css";

/** Visual tone of an alert. */
export type AlertTone = "info" | "warning" | "danger" | "success";

/**
 * How an alert is announced to screen readers (WCAG 4.1.3, UX-106).
 *
 * - `assertive`: interrupts (`role="alert"`), for errors that appear after an action.
 * - `polite`: waits for a pause (`role="status"`), for success and progress messages.
 * - `off`: no live region, for static callouts that are part of the content, such as findings, a
 *   hint or a banner that is present when the page loads.
 */
export type AlertLive = "assertive" | "polite" | "off";

const toneClass: Readonly<Record<AlertTone, string | undefined>> = {
  info: styles["alertInfo"],
  warning: styles["alertWarning"],
  danger: styles["alertDanger"],
  success: styles["alertSuccess"],
};

const roleByLive: Readonly<Record<AlertLive, "alert" | "status" | undefined>> = {
  assertive: "alert",
  polite: "status",
  off: undefined,
};

/** Properties of {@link Alert}. */
export interface AlertProps {
  /** Tone. */
  readonly tone: AlertTone;
  /**
   * Announcement. Defaults to `assertive` for the tone `danger` and `polite` otherwise; static
   * callouts pass `off`, so screen readers do not announce them on load.
   */
  readonly live?: AlertLive | undefined;
  /** Additional class name. */
  readonly className?: string | undefined;
  /**
   * Reference to the box, for example to move the focus to an error summary after a failed
   * submit (WCAG 2.4.3, UX-110).
   */
  readonly ref?: Ref<HTMLDivElement> | undefined;
  /** Tab index; `-1` makes the box focusable by script without adding it to the tab order. */
  readonly tabIndex?: number | undefined;
  /**
   * Marks the box as the error summary of its form, which `focusFirstInvalid` focuses when no
   * single control is invalid (`data-error-summary`; give it `tabIndex={-1}`).
   */
  readonly errorSummary?: boolean | undefined;
  /** Content. */
  readonly children: ReactNode;
}

/**
 * Message box. Whether and how screen readers announce it is chosen with `live`: live messages
 * that appear after an action are announced, static callouts are not. A focusable error summary
 * passes a `ref`, `tabIndex={-1}` and `errorSummary`.
 *
 * @param props - Component properties; `ref` is passed to the box.
 * @returns The alert element.
 */
export function Alert({ ref, ...props }: AlertProps): JSX.Element {
  const live = props.live ?? (props.tone === "danger" ? "assertive" : "polite");
  const className = cx(styles["alert"], toneClass[props.tone], props.className);
  const alert = (
    <div
      ref={ref}
      className={className}
      role={roleByLive[live]}
      tabIndex={props.tabIndex}
      data-error-summary={props.errorSummary === true ? "" : undefined}
    >
      {props.children}
    </div>
  );

  return alert;
}
